import {
  AlertCircle,
  Download,
  History,
  MoreHorizontal,
  Play,
  RefreshCw,
  RotateCcw,
  SearchX,
} from "lucide-react";
import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { useAuth } from "@/modules/auth/hooks/useAuth";
import { notificationsSocketService } from "@/modules/dashboard/notifications/services/notifications-socket.service";
import { useRuntimeWallets } from "@/modules/dashboard/wallets/hooks/useRuntimeWallets";
import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaIconButton } from "@/shared/components/buttons/ColocaIconButton";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { ColocaEmpty } from "@/shared/components/feedback/ColocaEmpty";
import { ColocaLineSkeleton } from "@/shared/components/feedback/ColocaSkeleton";
import { useAppShellBreadcrumb } from "@/shared/components/layout/useAppShellBreadcrumb";
import { NoAccessView } from "@/shared/components/NoAccessView";
import { type ColocaDropdownItem, ColocaDropdownMenu } from "@/shared/components/overlays/ColocaDropdownMenu";
import { ColocaTooltip } from "@/shared/components/overlays/ColocaTooltip";
import useTranslations from "@/shared/lang/useTranslations";
import { colocaToast } from "@/shared/utils/toast";

import { BatchApprovalBanner } from "../components/approval/BatchApprovalBanner";
import { type ApprovalViewer, BatchApprovalFooter } from "../components/approval/BatchApprovalFooter";
import { BatchReturnedBanner } from "../components/approval/BatchReturnedBanner";
import { RejectBatchDialog } from "../components/approval/RejectBatchDialog";
import { WithdrawBatchDialog } from "../components/approval/WithdrawBatchDialog";
import { BatchEventsDrawer } from "../components/detail/BatchEventsDrawer";
import { BatchExpiryBanner } from "../components/detail/BatchExpiryBanner";
import { BatchHeader } from "../components/detail/BatchHeader";
import { BatchResultBanner } from "../components/detail/BatchResultBanner";
import { BatchStepper } from "../components/detail/BatchStepper";
import { BatchValidatingPanel } from "../components/detail/BatchValidatingPanel";
import { BatchValidationFailedPanel } from "../components/detail/BatchValidationFailedPanel";
import { BatchWarningsBanner } from "../components/detail/BatchWarningsBanner";
import { CancelBatchDialog } from "../components/detail/CancelBatchDialog";
import { ExecuteBatchDialog } from "../components/execute/ExecuteBatchDialog";
import { ResumeBatchDialog } from "../components/execute/ResumeBatchDialog";
import { RetryFailedItemsDialog } from "../components/execute/RetryFailedItemsDialog";
import { BatchProgressPanel } from "../components/progress/BatchProgressPanel";
import { BatchBalancePanel } from "../components/review/BatchBalancePanel";
import { BatchItemDrawer } from "../components/review/BatchItemDrawer";
import { BatchItemsKpiStrip } from "../components/review/BatchItemsKpiStrip";
import { BatchItemsTable } from "../components/review/BatchItemsTable";
import { BatchItemsToolbar } from "../components/review/BatchItemsToolbar";
import { BatchReviewFooter } from "../components/review/BatchReviewFooter";
import { BatchTotalsPanel } from "../components/review/BatchTotalsPanel";
import { InsufficientBalanceSuggestions } from "../components/review/InsufficientBalanceSuggestions";
import { SubmitForApprovalDialog } from "../components/review/SubmitForApprovalDialog";
import {
  EXECUTION_ITEMS_FILTERS,
  type ItemsFilterId,
  REVIEW_ITEMS_FILTERS,
} from "../constants/payoutBatches.constants";
import { useBatchErrorMessage } from "../hooks/useBatchErrorMessage";
import { usePayoutBatchActions } from "../hooks/usePayoutBatchActions";
import { usePayoutBatchItems } from "../hooks/usePayoutBatchItems";
import { usePayoutBatchLive } from "../hooks/usePayoutBatchLive";
import { usePayoutBatchSelection } from "../hooks/usePayoutBatchSelection";
import {
  type PayoutBatchDetail,
  type PayoutBatchItem,
  PayoutBatchStatus,
} from "../types/payoutBatches.types";
import { batchPath, newBatchPath, PAYOUT_BATCHES_PATH, shortBatchId } from "../utils/batchLinks";
import {
  canApproveBatches,
  canApproveFromReview,
  canApproveThisBatch,
  canRejectThisBatch,
  canWriteBatches,
  isBatchMaker,
  isBatchSubmitter,
} from "../utils/batchPermissions";
import {
  batchRevision,
  canCancel,
  canResume,
  isExecutionStatus,
  isReviewStatus,
  runnableRows,
} from "../utils/batchStatus";

/**
 * `/app/payout-batches/:id` — one screen, three faces decided by the batch status: validating (a pulsing
 * bar), reviewing (balance, totals, the rows and their selection, the sticky ticket) and running/finished
 * (the strip, the outcomes, retry and resume). Everything below is keyed by id so a different batch starts
 * from a clean slate.
 */
type HandOffDialog = "submit" | "reject" | "withdraw" | "execute";

const PayoutBatchDetailPage = () => {
  const { id = "" } = useParams<{ id: string }>();
  const { t: tGlobal } = useTranslations("global");

  const breadcrumb = useMemo(
    () => [
      { label: tGlobal("sidebar_section_billeteras") },
      { label: tGlobal("sidebar_menu_payout_batches"), to: PAYOUT_BATCHES_PATH },
      { label: shortBatchId(id), to: batchPath(id) },
    ],
    [id, tGlobal],
  );
  useAppShellBreadcrumb(breadcrumb);

  return <BatchDetailContent key={id} id={id} />;
};

export default PayoutBatchDetailPage;

function BatchDetailContent({ id }: { id: string }) {
  const { t } = useTranslations("payoutBatches");
  const navigate = useNavigate();
  const { isInternalUser, user } = useAuth();
  const describeError = useBatchErrorMessage();

  const live = usePayoutBatchLive(id);
  const { batch, applyBatch, refresh } = live;
  const actions = usePayoutBatchActions(id, applyBatch);

  const batchRef = useRef<PayoutBatchDetail | null>(batch);
  useEffect(() => {
    batchRef.current = batch;
  });

  const executionMode = batch
    ? isExecutionStatus(batch.status) ||
      batch.status === PayoutBatchStatus.CANCELLED ||
      batch.status === PayoutBatchStatus.EXPIRED
    : false;
  const filters = executionMode ? EXECUTION_ITEMS_FILTERS : REVIEW_ITEMS_FILTERS;
  const [filterId, setFilterId] = useState<ItemsFilterId>("all");
  const [page, setPage] = useState(1);
  const activeFilter = filters.find((filter) => filter.id === filterId) ?? filters[0];

  const items = usePayoutBatchItems(id, activeFilter.query, page, live.revision, live.subscribeRowUpdates);
  const selection = usePayoutBatchSelection(batch, applyBatch, items);

  const wallets = useRuntimeWallets(isInternalUser ? batch?.organization_id : undefined);
  const liveWallet = wallets.wallets.find((wallet) => wallet.currency === batch?.source_currency) ?? null;

  const [openItem, setOpenItem] = useState<PayoutBatchItem | null>(null);
  const [eventsOpen, setEventsOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);
  const [retryOpen, setRetryOpen] = useState(false);
  const [resumeOpen, setResumeOpen] = useState(false);
  // Opened for one revision of the batch: a status change or a new submission closes it for good, so a dialog
  // never comes back open over a selection nobody asked to confirm.
  const [handOff, setHandOff] = useState<{ kind: HandOffDialog; revision: string } | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [rejectError, setRejectError] = useState<string | null>(null);

  const changeFilter = (next: ItemsFilterId) => {
    setFilterId(next);
    setPage(1);
  };

  const handleCancel = async (reason: string) => {
    setCancelError(null);
    const error = await actions.cancel(reason);
    if (error) {
      setCancelError(describeError(error));
      return;
    }
    colocaToast.success(t("cancel_success"));
    setCancelOpen(false);
  };

  const handleRevalidate = async () => {
    const error = await actions.revalidate();
    if (error) colocaToast.error(describeError(error));
  };

  const handleReport = async () => {
    const error = await actions.downloadReport();
    if (error) colocaToast.error(describeError(error));
  };

  const handleOpenFile = async () => {
    const error = await actions.openFile();
    if (error) colocaToast.error(describeError(error));
  };

  const handleSubmit = async () => {
    const fingerprint = batchRef.current?.summary.fingerprint;
    if (!fingerprint) return;
    setSubmitError(null);
    const error = await actions.submit(fingerprint);
    if (error) {
      setSubmitError(describeError(error));
      void refresh();
      return;
    }
    colocaToast.success(t("submit_success"));
    setHandOff(null);
  };

  const handleReject = async (reason: string) => {
    setRejectError(null);
    const error = await actions.reject(reason);
    if (error) {
      setRejectError(describeError(error));
      void refresh();
      return;
    }
    colocaToast.success(t("reject_success"));
    setHandOff(null);
  };

  const handleWithdraw = async () => {
    const error = await actions.withdraw();
    if (error) {
      colocaToast.error(describeError(error));
      setHandOff(null);
      void refresh();
      return;
    }
    colocaToast.success(t("withdraw_success"));
    setHandOff(null);
  };

  if (!live.hasAccess) return <NoAccessView />;

  if (live.isLoading) {
    return (
      <div className="mx-auto w-full max-w-[1400px] space-y-6 p-2">
        <ColocaLineSkeleton lines={2} width="1/2" />
        <ColocaLineSkeleton lines={4} />
      </div>
    );
  }

  if (!batch) {
    return (
      <div className="mx-auto w-full max-w-[1400px] space-y-6 p-2">
        {live.notFound ? (
          <ColocaEmpty icon={SearchX} title={t("not_found_title")} description={t("not_found_description")}>
            <ColocaButton variant="default" onClick={() => navigate(PAYOUT_BATCHES_PATH)}>
              {t("action_back_to_list")}
            </ColocaButton>
          </ColocaEmpty>
        ) : (
          <ColocaAlert
            variant="destructive"
            icon={AlertCircle}
            text={
              <span className="flex flex-wrap items-center gap-3">
                {live.error ? describeError(live.error) : t("error_load")}
                <ColocaButton variant="default" size="xs" icon={RefreshCw} onClick={() => void refresh()}>
                  {t("error_retry")}
                </ColocaButton>
              </span>
            }
          />
        )}
      </div>
    );
  }

  // What this principal may do on this batch (utils/batchPermissions.ts). The backend enforces the same.
  const canWrite = canWriteBatches(user);
  const canApprove = canApproveBatches(user);
  const canApproveThis = canApproveThisBatch(user, batch);
  const canReject = canRejectThisBatch(user, batch);
  const approvesFromReview = canApproveFromReview(user, batch);
  const isMaker = isBatchMaker(user, batch);
  const approvalViewer: ApprovalViewer = canReject
    ? "approver"
    : canApproveThis
      ? "self_approver"
      : isMaker
        ? "maker"
        : "reader";
  const selfApproval = canApproveThis && isMaker ? (batch.summary.approval_policy?.reason ?? null) : null;
  // Rows an earlier run left to pay would join the retry's run and spend its cap: the backend asks to resume first.
  const retryBlockedHint = !canApproveThis
    ? t("approval_uploader_hint")
    : runnableRows(batch.counters) > 0
      ? t("retry_resume_first_hint")
      : null;
  // A failed validation can run again once its cause is fixed outside the file (verification, rail switch), but
  // only over stored rows: a rejected upload has none, and the way out there is another file.
  const canRevalidateFailed =
    batch.status === PayoutBatchStatus.VALIDATION_FAILED && batch.counters.total > 0;

  const menuItems: ColocaDropdownItem[] = [
    {
      label: t("action_view_file"),
      icon: Download,
      onClick: () => void handleOpenFile(),
      disabled: actions.pending.file,
    },
    ...(canWrite && (batch.status === PayoutBatchStatus.READY || canRevalidateFailed)
      ? [
          {
            label: t("action_revalidate"),
            icon: RefreshCw,
            onClick: () => void handleRevalidate(),
            disabled: actions.pending.revalidate,
          },
        ]
      : []),
    ...(executionMode &&
    batch.status !== PayoutBatchStatus.CANCELLED &&
    batch.status !== PayoutBatchStatus.EXPIRED
      ? [
          {
            label: t("action_download_report"),
            icon: Download,
            onClick: () => void handleReport(),
            disabled: actions.pending.report,
          },
        ]
      : []),
    ...(canWrite && canCancel(batch.status)
      ? [
          {
            label: t("action_cancel_batch"),
            variant: "destructive" as const,
            onClick: () => setCancelOpen(true),
          },
        ]
      : []),
  ];

  const revision = batchRevision(batch);
  const openHandOff = (kind: HandOffDialog) => setHandOff({ kind, revision });
  const isHandOffOpen = (kind: HandOffDialog) => handOff?.kind === kind && handOff.revision === revision;
  const handOffOpenChange = (kind: HandOffDialog) => (open: boolean) =>
    open ? openHandOff(kind) : setHandOff(null);

  const isReady = batch.status === PayoutBatchStatus.READY;
  const isPendingApproval = batch.status === PayoutBatchStatus.PENDING_APPROVAL;
  const isReview = isReviewStatus(batch.status);
  const reviewCounts = {
    all: batch.counters.total,
    selected: batch.counters.selected,
    errors: batch.counters.invalid,
    excluded: batch.counters.excluded,
  };
  const executionCounts = {
    all: batch.counters.total,
    processing: batch.counters.processing + batch.counters.queued,
    submitted: batch.counters.submitted,
    paid: batch.counters.paid,
    failed: batch.counters.failed,
    reversed: batch.counters.reversed,
  };

  return (
    <div className="mx-auto w-full max-w-[1400px] space-y-6 p-2">
      <BatchHeader
        batch={batch}
        actions={
          <>
            <ColocaButton variant="default" size="sm" icon={History} onClick={() => setEventsOpen(true)}>
              {t("action_history")}
            </ColocaButton>
            <ColocaDropdownMenu
              trigger={
                <ColocaIconButton
                  variant="default"
                  size="sm"
                  icon={MoreHorizontal}
                  aria-label={t("action_history")}
                />
              }
              items={menuItems}
              align="end"
            />
          </>
        }
      />

      <BatchStepper status={batch.status} className="max-w-xl" />

      {(batch.status === PayoutBatchStatus.UPLOADED || batch.status === PayoutBatchStatus.VALIDATING) && (
        <BatchValidatingPanel rowCount={batch.row_count} />
      )}

      {batch.status === PayoutBatchStatus.VALIDATION_FAILED && (
        <BatchValidationFailedPanel
          blockers={batch.summary.blockers}
          failureReason={batch.failure_reason}
          onUploadAnother={canWrite ? () => navigate(newBatchPath()) : undefined}
          onCancel={canWrite ? () => setCancelOpen(true) : undefined}
          isCancelling={actions.pending.cancel}
          onRevalidate={canWrite && canRevalidateFailed ? () => void handleRevalidate() : undefined}
          isRevalidating={actions.pending.revalidate}
        />
      )}

      {isReview && (
        <>
          {isPendingApproval && (
            <BatchApprovalBanner
              submittedAt={batch.submitted_at}
              submittedByViewer={isBatchSubmitter(user, batch)}
              viewerMayApprove={canApproveThis}
              linksToSettings={!isInternalUser}
            />
          )}
          {isReady && batch.return_reason && (
            <BatchReturnedBanner reason={batch.return_reason} returnedAt={batch.returned_at} />
          )}
          <BatchExpiryBanner expiresAt={batch.expires_at} onExpire={() => void refresh()} />
          <BatchWarningsBanner
            warnings={batch.summary.warnings}
            onRevalidate={canWrite && isReady ? () => void handleRevalidate() : undefined}
            isRevalidating={actions.pending.revalidate}
          />
          {batch.summary.blockers.length > 0 && (
            <ColocaAlert
              variant="destructive"
              icon={AlertCircle}
              text={
                <ul className="list-disc space-y-1 pl-4">
                  {batch.summary.blockers.map((blocker, index) => (
                    <li key={`${blocker.code}-${index}`}>{blocker.message}</li>
                  ))}
                </ul>
              }
            />
          )}
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <BatchBalancePanel
              batch={batch}
              liveAvailable={liveWallet?.balance_available ?? null}
              liveWalletStatus={liveWallet?.status ?? null}
              isInternal={!!isInternalUser}
            />
            <BatchTotalsPanel batch={batch} />
          </div>
          {isReady &&
            canWrite &&
            batch.summary.wallet &&
            !batch.summary.wallet.sufficient &&
            batch.summary.suggestions && (
              <InsufficientBalanceSuggestions
                suggestions={batch.summary.suggestions}
                sourceCurrency={batch.source_currency}
                isApplying={selection.isSyncing}
                onApply={(itemIds) => void selection.bulk("replace", itemIds)}
              />
            )}
          <BatchItemsKpiStrip
            filters={filters}
            counts={reviewCounts}
            active={activeFilter.id}
            onChange={changeFilter}
          />
          {isReady && canWrite && (
            <BatchItemsToolbar
              onSelectAllValid={() => void selection.bulk("select_all_valid")}
              onDeselectAll={() => void selection.bulk("deselect_all")}
              onExcludeWithWarnings={() => void selection.bulk("exclude_with_warnings")}
              disabled={selection.isSyncing}
              isSyncing={selection.isSyncing}
            />
          )}
          {selection.error && (
            <ColocaAlert variant="destructive" icon={AlertCircle} text={describeError(selection.error)} />
          )}
        </>
      )}

      {executionMode && (
        <>
          <BatchResultBanner batch={batch} />
          {batch.status !== PayoutBatchStatus.CANCELLED && batch.status !== PayoutBatchStatus.EXPIRED && (
            <BatchProgressPanel
              batch={batch}
              lastUpdatedAt={live.lastUpdatedAt}
              isLive={live.isLive}
              isSocketConnected={notificationsSocketService.isConnected()}
            />
          )}
          <div className="flex flex-wrap items-center justify-between gap-3">
            <BatchItemsKpiStrip
              filters={filters}
              counts={executionCounts}
              active={activeFilter.id}
              onChange={changeFilter}
            />
          </div>
          <div className="flex flex-wrap gap-2">
            {batch.counters.failed > 0 &&
              !live.isLive &&
              canApprove &&
              (retryBlockedHint ? (
                <ColocaTooltip
                  trigger={
                    <ColocaButton variant="primary" icon={RotateCcw} disabled>
                      {t("action_retry_failed", {
                        count: batch.counters.failed,
                      })}
                    </ColocaButton>
                  }
                >
                  {retryBlockedHint}
                </ColocaTooltip>
              ) : (
                <ColocaButton variant="primary" icon={RotateCcw} onClick={() => setRetryOpen(true)}>
                  {t("action_retry_failed", { count: batch.counters.failed })}
                </ColocaButton>
              ))}
            {canApprove && canResume(batch) && (
              <ColocaButton variant="default" icon={Play} onClick={() => setResumeOpen(true)}>
                {t("action_resume")}
              </ColocaButton>
            )}
            {canWrite &&
              (batch.status === PayoutBatchStatus.CANCELLED ||
                batch.status === PayoutBatchStatus.EXPIRED) && (
                <ColocaButton variant="primary" onClick={() => navigate(newBatchPath())}>
                  {t("action_upload_another")}
                </ColocaButton>
              )}
          </div>
        </>
      )}

      {(isReview || executionMode) && (
        <BatchItemsTable
          mode={isReview ? "review" : "execution"}
          batch={batch}
          rows={items.rows}
          isLoading={items.isLoading}
          page={page}
          total={items.total}
          onPageChange={setPage}
          onRowClick={setOpenItem}
          selection={isReady && canWrite ? selection : undefined}
          selectionDisabled={selection.isBulkPending}
        />
      )}

      {isReady && (
        <BatchReviewFooter
          batch={batch}
          isSyncing={selection.isSyncing}
          canSubmit={canWrite}
          canApprove={approvesFromReview}
          onSubmit={() => {
            setSubmitError(null);
            openHandOff("submit");
          }}
          onApprove={() => openHandOff("execute")}
        />
      )}

      {isPendingApproval && (
        <BatchApprovalFooter
          batch={batch}
          viewer={approvalViewer}
          canWithdraw={canWrite}
          onApprove={() => openHandOff("execute")}
          onReject={() => {
            setRejectError(null);
            openHandOff("reject");
          }}
          onWithdraw={() => openHandOff("withdraw")}
          isWithdrawing={actions.pending.withdraw}
        />
      )}

      <BatchItemDrawer
        item={openItem}
        batch={batch}
        open={openItem !== null}
        onOpenChange={(open) => !open && setOpenItem(null)}
      />
      <BatchEventsDrawer
        batchId={id}
        open={eventsOpen}
        onOpenChange={setEventsOpen}
        revision={live.revision}
        isLive={live.isLive}
      />
      <CancelBatchDialog
        open={cancelOpen}
        onOpenChange={(open) => {
          setCancelOpen(open);
          if (!open) setCancelError(null);
        }}
        onConfirm={handleCancel}
        isPending={actions.pending.cancel}
        errorMessage={cancelError}
      />
      {/* Remounted per revision: a dialog reopened after the batch changed starts unconfirmed again. */}
      <Fragment key={revision}>
        {isReady && (
          <SubmitForApprovalDialog
            batch={batch}
            open={isHandOffOpen("submit")}
            onOpenChange={handOffOpenChange("submit")}
            onConfirm={handleSubmit}
            isPending={actions.pending.submit}
            errorMessage={submitError}
          />
        )}
        {isPendingApproval && canReject && (
          <RejectBatchDialog
            open={isHandOffOpen("reject")}
            onOpenChange={handOffOpenChange("reject")}
            onConfirm={handleReject}
            isPending={actions.pending.reject}
            errorMessage={rejectError}
          />
        )}
        {isPendingApproval && (
          <WithdrawBatchDialog
            open={isHandOffOpen("withdraw")}
            onOpenChange={handOffOpenChange("withdraw")}
            onConfirm={() => void handleWithdraw()}
            isPending={actions.pending.withdraw}
          />
        )}
        {((isPendingApproval && canApproveThis) || approvesFromReview) && (
          <ExecuteBatchDialog
            batch={batch}
            open={isHandOffOpen("execute")}
            onOpenChange={handOffOpenChange("execute")}
            isInternal={!!isInternalUser}
            applyBatch={applyBatch}
            refresh={refresh}
            selfApproval={selfApproval}
          />
        )}
      </Fragment>
      {executionMode && (
        <>
          {canApproveThis && (
            <RetryFailedItemsDialog
              batch={batch}
              open={retryOpen}
              onOpenChange={setRetryOpen}
              applyBatch={applyBatch}
              onRetried={() => void items.refresh()}
            />
          )}
          <ResumeBatchDialog
            batch={batch}
            open={resumeOpen}
            onOpenChange={setResumeOpen}
            applyBatch={applyBatch}
          />
        </>
      )}
    </div>
  );
}
